/**
 * Draft Conflict Service
 * Handles detection and resolution of draft conflicts
 */

export interface DraftVersion {
  answers: Record<string, any>;
  updatedAt: string;
  hash: string;
}

export interface ConflictResult {
  hasConflict: boolean;
  localVersion?: DraftVersion;
  serverVersion?: DraftVersion;
  conflictingFields?: string[];
}

/**
 * Create a simple hash of draft answers for comparison
 */
export function hashDraftAnswers(answers: Record<string, any>): string {
  const sorted = Object.keys(answers)
    .sort()
    .map(key => `${key}:${JSON.stringify(answers[key])}`)
    .join('|');
  
  // Simple hash function
  let hash = 0;
  for (let i = 0; i < sorted.length; i++) {
    const char = sorted.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return hash.toString(36);
}

/**
 * Detect conflicts between local and server drafts
 */
export function detectConflict(
  localAnswers: Record<string, any>,
  localUpdatedAt: string,
  serverAnswers: Record<string, any>,
  serverUpdatedAt: string
): ConflictResult {
  const localHash = hashDraftAnswers(localAnswers);
  const serverHash = hashDraftAnswers(serverAnswers);
  
  // No conflict if hashes match
  if (localHash === serverHash) {
    return { hasConflict: false };
  }
  
  // Find conflicting fields
  const conflictingFields: string[] = [];
  const allKeys = new Set([...Object.keys(localAnswers), ...Object.keys(serverAnswers)]);
  
  for (const key of allKeys) {
    const localVal = JSON.stringify(localAnswers[key]);
    const serverVal = JSON.stringify(serverAnswers[key]);
    if (localVal !== serverVal) {
      conflictingFields.push(key);
    }
  }
  
  return {
    hasConflict: true,
    localVersion: {
      answers: localAnswers,
      updatedAt: localUpdatedAt,
      hash: localHash,
    },
    serverVersion: {
      answers: serverAnswers,
      updatedAt: serverUpdatedAt,
      hash: serverHash,
    },
    conflictingFields,
  };
}

export type MergeStrategy = 'local' | 'server' | 'newest';

/**
 * Merge drafts based on strategy
 */
export function mergeDrafts(
  local: DraftVersion,
  server: DraftVersion,
  strategy: MergeStrategy
): Record<string, any> {
  switch (strategy) {
    case 'local':
      return { ...local.answers };
    case 'server':
      return { ...server.answers };
    case 'newest':
      const localTime = new Date(local.updatedAt).getTime();
      const serverTime = new Date(server.updatedAt).getTime();
      return localTime > serverTime ? { ...local.answers } : { ...server.answers };
    default:
      return { ...server.answers };
  }
}

/**
 * Format field name for display
 */
export function formatFieldName(field: string): string {
  return field
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
}

/**
 * Format value for display
 */
export function formatValueForDisplay(value: any): string {
  if (value === null || value === undefined) return 'Not set';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') {
    if (Number.isInteger(value)) return value.toString();
    return value.toFixed(2);
  }
  return String(value);
}
